import type { Plan } from "@/services/types"
import { cn } from "@/shared/lib/cn"
import { openWhatsApp } from "@/shared/lib/whatsapp"
import { ArrowRightSm, CheckCircle, Download, StarFilled } from "@/shared/ui/icons"

/** Selo do topo do card. Sem `tag`, fica apenas um fio de separacao. */
function PlanRibbon({ tag, featured }: { tag: string; featured: boolean }) {
  if (!tag) return <div className="h-px bg-verde-profundo/6" />

  return (
    <div
      className="px-4 py-2.5 flex items-center justify-center gap-2"
      style={{ background: featured ? "#2E3724" : "#F3F2EE" }}
    >
      {featured && <StarFilled />}
      <span
        className={cn(
          "text-[11px] font-bold tracking-widest uppercase",
          featured ? "text-white" : "text-verde-profundo/50",
        )}
      >
        {tag}
      </span>
    </div>
  )
}

export interface PlanCardProps {
  plan: Plan
}

export function PlanCard({ plan }: PlanCardProps) {
  // O card lista o que o plano cobre. Os itens marcados como nao cobertos
  // aparecem no comparativo e no PDF — repeti-los aqui como "recurso" com um X
  // ao lado so confunde a leitura rapida.
  const included = plan.highlights.filter((highlight) => highlight.ok)

  return (
    <article
      className={cn(
        "relative flex flex-col h-full rounded-[24px] overflow-hidden transition-all duration-500 hover:-translate-y-1 bg-white",
        plan.featured
          ? "border-2 border-verde-profundo shadow-2xl shadow-verde-profundo/12"
          : "border border-verde-profundo/12 shadow-md hover:shadow-xl hover:border-verde-profundo/22",
      )}
    >
      <PlanRibbon tag={plan.tag} featured={plan.featured} />

      <div className="px-6 pt-5 pb-4 border-b border-verde-profundo/6">
        <h3 className="text-base font-semibold text-verde-profundo leading-snug mb-1">{plan.name}</h3>
        <p className="text-[11px] text-verde-profundo/45 leading-snug mb-4">{plan.indication}</p>

        <p className="flex items-baseline gap-1.5 flex-wrap">
          <span className="text-[32px] font-bold text-verde-profundo tracking-tight leading-none">
            {plan.price.replace("/mês", "")}
          </span>
          <span className="text-sm text-verde-profundo/40 font-medium">/mês</span>
        </p>

        {plan.adhesion ? (
          <p className="text-xs text-verde-profundo/40 mt-1.5">{plan.adhesion}</p>
        ) : (
          <p className="text-xs text-dourado-escuro font-semibold mt-1.5">Sem taxa de adesão</p>
        )}
      </div>

      <ul className="flex flex-col gap-2.5 px-6 py-5 flex-1">
        {included.map((highlight) => (
          <li
            key={highlight.text}
            className="flex items-start gap-2.5 text-[13px] text-verde-profundo/75 leading-snug"
          >
            <CheckCircle className="shrink-0 mt-0.5" />
            <span>{highlight.text}</span>
          </li>
        ))}
      </ul>

      <div className="px-6 pb-3">
        <a
          href={plan.pdfUrl}
          download={plan.pdfFilename}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-2 text-xs font-semibold text-dourado-escuro hover:text-verde-profundo transition-colors border border-dourado/25 hover:border-verde-profundo/20 rounded-xl"
        >
          <Download />
          Baixar detalhes do plano (PDF)
        </a>
      </div>

      <div className="px-6 pb-6 pt-1">
        <button
          type="button"
          onClick={() => openWhatsApp(plan.name)}
          className={cn(
            "w-full h-11 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 cursor-pointer",
            plan.featured
              ? "bg-verde-profundo text-white hover:bg-verde-medio shadow-md hover:shadow-lg hover:-translate-y-0.5"
              : "bg-off-white text-verde-profundo hover:bg-verde-profundo hover:text-white",
          )}
        >
          {plan.ctaLabel}
          <ArrowRightSm />
        </button>
      </div>
    </article>
  )
}
